import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUsers, updateUserRole, deleteUser, FlowForgeUser } from '../api/adminUsers';
import { useAuth } from '../auth/AuthContext';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { PageHeader } from '../components/PageHeader';

const ROLES = ['Viewer', 'Editor', 'Admin'] as const;

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function AdminUsersPage() {
  const { data: users, isLoading, isError } = useQuery({
    queryKey: ['admin-users'],
    queryFn: getUsers,
  });
  const qc = useQueryClient();
  const { userId: currentUserId } = useAuth();

  const [pendingRoleId, setPendingRoleId] = useState<string | null>(null);

  const roleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: FlowForgeUser['role'] }) => updateUserRole(id, role),
    onMutate: ({ id }) => setPendingRoleId(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
    onError: () => qc.invalidateQueries({ queryKey: ['admin-users'] }),
    onSettled: () => setPendingRoleId(null),
  });

  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: () => {
      setConfirmDelete(null);
      qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  if (isLoading) return <p className="text-muted-foreground">Loading…</p>;
  if (isError) return <p className="text-destructive">Failed to load users. Please refresh.</p>;

  return (
    <div>
      <PageHeader
        title="Users"
        description="Azure AD users who have signed in. Role changes take effect on next request."
      />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Email</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Last login</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {users?.map((user: FlowForgeUser) => {
            const isSelf = user.id === currentUserId;
            return (
              <TableRow key={user.id}>
                <TableCell>{user.email}</TableCell>
                <TableCell className="text-muted-foreground">{user.displayName}</TableCell>
                <TableCell>
                  <Select
                    value={user.role}
                    disabled={isSelf || pendingRoleId === user.id}
                    onValueChange={v => roleMutation.mutate({ id: user.id, role: v as FlowForgeUser['role'] })}
                  >
                    <SelectTrigger className="h-7 w-28 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map(r => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {user.lastLoginAt ? formatRelative(user.lastLoginAt) : '—'}
                </TableCell>
                <TableCell>
                  {!isSelf && (
                    confirmDelete === user.id ? (
                      <span className="flex items-center gap-2 justify-end">
                        <Button
                          variant="ghost"
                          size="xs"
                          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => deleteMutation.mutate(user.id)}
                        >
                          Confirm
                        </Button>
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => setConfirmDelete(null)}
                        >
                          Cancel
                        </Button>
                      </span>
                    ) : (
                      <div className="flex justify-end">
                        <Button
                          variant="ghost"
                          size="xs"
                          className="text-muted-foreground hover:text-destructive"
                          onClick={() => setConfirmDelete(user.id)}
                        >
                          Remove
                        </Button>
                      </div>
                    )
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          {users?.length === 0 && (
            <TableRow>
              <TableCell colSpan={5} className="p-6 text-center text-muted-foreground">
                No Azure AD users yet
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
